import useSWR from "swr";
import { ProductSummary } from "@/common/productSummary/types";
import { ProductSearchListItemProps } from "../components/v2/ProductSearchListItem";

interface BeforeTypingProducts {
  recentlyViewed: ProductSearchListItemProps[];
  featuredPrograms: ProductSearchListItemProps[];
  isLoading: boolean;
  error: Error | undefined;
}

const convertProductSummaryToSearchListItem = (
  product: ProductSummary,
): ProductSearchListItemProps => {
  const partnerFullName = Array.isArray(product.partnerName)
    ? product.partnerName[0]
    : product.partnerName;
  const partnerKey = product.partnerKeys?.[0] ?? "";

  return {
    id: product.productUuid,
    productName: product.productName,
    productCategory: product.productCategory ?? product.productType,
    partner: {
      key: partnerKey,
      fullName: partnerFullName,
    },
    partnerLogoUrl: product.partnerLogoOverride ?? product.partnerLogoUrl,
    timeToComplete: product.weeksToComplete ?? undefined,
    href: `/${product.productSlug}`,
  };
};

const fetchProductSummaries = async (
  uuids: string[],
): Promise<ProductSummary[]> => {
  if (uuids.length === 0) return [];

  const response = await fetch("/api/product-summaries", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uuids }),
  });

  if (!response.ok) {
    throw new Error("Failed to fetch product summaries");
  }

  return response.json();
};

export const useBeforeTypingProducts = (
  recentlyViewedUUIDs: string[],
  featuredProgramsUUIDs: string[],
): BeforeTypingProducts => {
  // Deduplicate input arrays to prevent duplicate entries
  const uniqueRecentlyViewedUUIDs = Array.from(new Set(recentlyViewedUUIDs));
  const uniqueFeaturedProgramsUUIDs = Array.from(
    new Set(featuredProgramsUUIDs),
  );

  // Cap recently viewed to max 2 items
  const limitedRecentlyViewedUUIDs = uniqueRecentlyViewedUUIDs.slice(0, 2);

  // Fetch up to 5 featured programs (don't filter out recently viewed here)
  // The display component will handle deduplication dynamically
  const limitedFeaturedProgramsUUIDs = uniqueFeaturedProgramsUUIDs.slice(0, 5);

  // Deduplicate combined array to avoid fetching same product twice
  const allUUIDs = Array.from(
    new Set([...limitedRecentlyViewedUUIDs, ...limitedFeaturedProgramsUUIDs]),
  );

  const {
    data: products,
    error,
    isLoading,
  } = useSWR(
    allUUIDs.length > 0 ? ["api/product-summaries", ...allUUIDs] : null,
    () => fetchProductSummaries(allUUIDs),
  );

  if (!products) {
    return { recentlyViewed: [], featuredPrograms: [], isLoading, error };
  }

  // Split products into recently viewed vs featured
  const recentlyViewedSet = new Set(limitedRecentlyViewedUUIDs);
  const featuredSet = new Set(limitedFeaturedProgramsUUIDs);

  const recentlyViewed: ProductSearchListItemProps[] = [];
  const featuredPrograms: ProductSearchListItemProps[] = [];
  const processedUUIDs = new Set<string>();

  for (const product of products) {
    // Skip if already processed to prevent duplicates
    if (processedUUIDs.has(product.productUuid)) {
      continue;
    }

    const convertedProduct = convertProductSummaryToSearchListItem(product);

    // Add to recently viewed if it's in that set
    if (recentlyViewedSet.has(product.productUuid)) {
      recentlyViewed.push(convertedProduct);
    }

    // Add to featured if it's in that set (can be in both)
    if (featuredSet.has(product.productUuid)) {
      featuredPrograms.push(convertedProduct);
    }

    processedUUIDs.add(product.productUuid);
  }

  return { recentlyViewed, featuredPrograms, isLoading, error };
};
